// Percorso file: src/pages/Reminders.jsx
//
// MEMBER REMINDERS — cantiere MEMBER-REMINDERS, Fase 1 (25/9/2026).
//
// La sezione con cui l'amministratore spedisce A MANO i solleciti ai soci.
// Due tipi accesi: «Complete your data» (l'email di completamento che il
// socio riceve all'approvazione, `inviaEmailApprovazioneSocio` sul backend,
// rispedita ai soci che la User List mostra come INCOMPLETE) e, dal cantiere
// PLAYFOOTBALL, «Play Football» (il promemoria a chi non ha ancora registrato
// Play Football per l'anno d'iscrizione; per i minori scrive al tutore — v.
// il blocco PLAY FOOTBALL più sotto) e, dal cantiere PAYMENTS, «Payments» (il
// sollecito a chi ha un saldo dovuto > 0 per l'anno GUARDATO, operativo o
// d'iscrizione; per i minori scrive al tutore — v. il blocco PAYMENTS) e, dal
// cantiere REMINDER-NON-RINNOVATI, «Renew membership» (l'invito a chi non ha
// rinnovato per l'anno guardato; regola = titolo al rinnovo del server,
// archiviati inclusi, invio solo sull'anno d'iscrizione — v. il blocco
// RENEW MEMBERSHIP).
// NESSUNO SCHEDULER: parte tutto dal pulsante Send, dopo una conferma che
// nomina il tipo.
//
// ⚠️ LA LISTA È QUELLA DELLA USER LIST, NON UNA SUA PARENTE. La riga
// INCOMPLETE di UsersList.jsx (a filtri vuoti) è: in coorte dell'annata,
// stato dell'annata né ARCHIVED né PENDING, annata guardata = annata
// d'iscrizione, `mustCompleteSeason === true`. Qui la stessa definizione,
// con gli stessi helper esportati, più una condizione: l'email non vuota.
// Chi cambia la definizione là deve guardare anche qui.
//
// ⚠️ La lista è solo una PROPOSTA: il server rivaluta ogni persona prima di
// spedire (autorità sul bersaglio, flag, minore, indirizzo) e risponde col
// riepilogo degli esiti.
//
// ⚠️ I MINORI FUORI DALLA LISTA (revisione D2): è esclusa chi ha un genitore
// collegato (`guardians` non vuoto). Il metro è «ha un genitore», non «senza
// login» (un adulto da completare spesso non ha ancora fatto login) e non la
// data di nascita. Per questo il numero NON coincide più con la User List, ed
// è voluto. Il server continua a scartare i minori all'invio («minor»): rete.
//
// Il controllo d'accesso NON sta qui: sta in App.jsx (RequireRole
// ADMIN/SUPERADMIN) e, quello vero, sul backend.

import React, { useEffect, useMemo, useState } from "react";
import { api, TIMEOUT_LUNGO_MS, TIMEOUT_INVIO_MESSAGE } from "../api/api";
import { useAuth } from "../context/AuthContext";
import { hasRole, normalizza } from "../utils/roleUtils";
import {
  squadreDelSoggetto,
  statoDelSoggetto,
  tessereDelSoggetto
} from "../utils/squadre";
import { annoDiLista } from "../utils/stagione";
import { isPlayFootballEligible } from "../utils/playFootball";
import { quoteDellAnnata, totaleVersato } from "../utils/quote";
import { denaroAschermo } from "../utils/denaro";
import { etaCompiuta, oggiAPerth } from "../utils/enrolDati";
import HomeShell from "../components/HomeShell";
import SeasonStatusBar from "../components/SeasonStatusBar";
import "../components/AdminTools.css";
import "./Reminders.css";

// ⚠️ RICOPIA DICHIARATA (decisione D2) dei due helper locali di
// UsersList.jsx, che non li esporta e che questo cantiere non tocca. I
// corpi sono quelli di là, invariati: chi cambia l'uno cambi l'altro.
const statoDi = (u) => {
  const s = normalizza(u?.status);
  if (s === "") return "ACTIVE";
  return (s === "ACTIVE" || s === "PENDING" || s === "ARCHIVED") ? s : "";
};

const eAmministratore = (user) => hasRole(user, "ADMIN", "SUPERADMIN");

// Il tetto del backend (POST /reminders/complete-data): oltre, il server
// rifiuta senza spedire. Qui si dice prima, per non far partire un rifiuto.
const TETTO_PER_INVIO = 100;

// I tipi di sollecito.
const TIPI = [
  { valore: "complete-data", etichetta: "Complete your data" },
  { valore: "payments", etichetta: "Payments" },
  { valore: "play-football", etichetta: "Play Football" },
  // ULTIMA voce del menù (decisione del committente, cantiere
  // REMINDER-NON-RINNOVATI): v. il blocco RENEW MEMBERSHIP più sotto.
  { valore: "renew", etichetta: "Renew membership" }
];

const etichettaDelTipo = (valore) =>
  (TIPI.find((t) => t.valore === valore) || {}).etichetta || "";

// Le voci del riepilogo, nell'ordine in cui si leggono. Le chiavi che un tipo
// non restituisce non compaiono (si mostrano solo i conteggi > 0).
const ESITI = [
  { chiave: "sent", etichetta: "Sent" },
  { chiave: "failed", etichetta: "Failed to send" },
  {
    chiave: "not-eligible",
    etichetta: "No longer incomplete",
    etichettaPF: "Skipped: nothing to register",
    etichettaPay: "Skipped: nothing outstanding",
    etichettaRenew: "Skipped: nothing to renew"
  },
  { chiave: "archived", etichetta: "Skipped: archived" },
  { chiave: "minor", etichetta: "Skipped: minor" },
  { chiave: "no-email", etichetta: "Skipped: no email" },
  { chiave: "not-allowed", etichetta: "Not allowed" },
  { chiave: "not-found", etichetta: "Not found" }
];

// L'esito dell'avviso di tracciamento alla segreteria (campo `trackingNotice`).
const AVVISO = {
  sent: "sent",
  failed: "failed — the reminders to members are still valid",
  "not-sent": "not sent (no reminder was sent)"
};

const emailDi = (u) => String(u?.email || "").trim();

// «Ha un genitore»: almeno un id non vuoto in `guardians`.
const haUnGenitore = (u) =>
  (Array.isArray(u?.guardians) ? u.guardians : []).some(
    (id) => String(id || "").trim() !== ""
  );

const nomeDi = (u) => `${u?.firstName || ""} ${u?.lastName || ""}`.trim();

// Ordine per cognome, metro di TeamsList.jsx (`perCognome`).
const perCognome = (a, b) =>
  normalizza(`${a?.lastName || ""} ${a?.firstName || ""}`).localeCompare(
    normalizza(`${b?.lastName || ""} ${b?.firstName || ""}`)
  );

/* =========================================================================
   PLAY FOOTBALL — chi va sollecitato, e a chi si scrive
   =========================================================================
   Il metro è quello della card Play Football della User List (ricognizione
   PLAYFOOTBALL §2.2), e il server lo ricalca in `daSollecitarePlayFootball`
   (POST /reminders/play-football), che RIVALUTA TUTTO: questa lista è solo
   una proposta.
   ⚠️ RICOPIE DICHIARATE: `personaDellAnnata` e `playFootballDellAnnata` sono
   locali a UsersList.jsx, che non le esporta e che questo cantiere non tocca.
   Corpi invariati: chi cambia l'uno cambi l'altro. */
const personaDellAnnata = (user, anno) => ({
  ...user,
  contexts: tessereDelSoggetto(user?.contexts, anno, {
    includiAmministrative: true,
  }),
});

const playFootballDellAnnata = (user, anno) => {
  const vuoto = { registered: false, feePaidByClub: false };

  if (anno === null) return vuoto;

  const mappa = user?.playFootballSeasons;

  if (!mappa || typeof mappa !== "object" || Array.isArray(mappa)) return vuoto;

  const chiave = Object.keys(mappa).find((k) => annoDiLista(k) === anno);

  if (chiave === undefined) return vuoto;

  return {
    registered: Boolean(mappa[chiave]?.registered),
    feePaidByClub: Boolean(mappa[chiave]?.feePaidByClub)
  };
};

// Da sollecitare nell'anno: non ARCHIVED, in coorte, stato dell'anno ACTIVE,
// idoneo (PLAYER/COACH sull'annata), `registered` dell'anno non vero (la
// quota pagata dal club non conta).
const daSollecitarePlayFootball = (u, anno) => {
  if (statoDi(u) === "ARCHIVED") return false;

  const inCoorte =
    tessereDelSoggetto(u?.contexts, anno, { stati: null }).length > 0 ||
    eAmministratore(u);
  if (!inCoorte) return false;

  if (statoDelSoggetto(u?.contexts, anno, { ripiegoPersona: statoDi(u) }) !== "ACTIVE") {
    return false;
  }

  if (!isPlayFootballEligible(personaDellAnnata(u, anno))) return false;

  return playFootballDellAnnata(u, anno).registered !== true;
};

// IL MINORE, COL METRO DEL SERVER SULLA DATA DI NASCITA (`isMinorDaDob`, con
// la forma ammessa da `dataDiNascitaValida`: «YYYY-M-D», eventuale ora in
// coda), portata a «YYYY-MM-DD» per `etaCompiuta`, che il file dichiara
// uguale a `isMinorDaDob`. ⚠️ `isMinor` non arriva in lista (la proiezione
// non lo emette, e non si tocca): un minore «solo per isMinor» qui sembra
// adulto — il server lo scarta («minor»). Il conteggio del 25/9 ne dà 0 per
// il 2027.
const eMinoreDaDob = (u, oggi) => {
  const pezzi = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ].*)?$/.exec(
    String(u?.dob ?? "").trim()
  );
  if (!pezzi) return false;

  const dob = `${pezzi[1]}-${pezzi[2].padStart(2, "0")}-${pezzi[3].padStart(2, "0")}`;
  const eta = etaCompiuta(dob, oggi);
  return eta !== null && eta < 18;
};

/* =========================================================================
   PAYMENTS — chi va sollecitato per le quote dell'anno guardato
   =========================================================================
   Stesso metro di coorte e stato di Play Football, senza filtro di ruolo
   (deve quadrare con la card Outstanding Fees), e saldo dell'anno > 0 con la
   regola della User List (colonna Balance): `quoteDellAnnata` + `totaleVersato`.
   Il server lo ricalca in `daSollecitarePagamenti` (POST /reminders/payments),
   che RIVALUTA TUTTO: questa lista è solo una proposta. */
const quoteDiRiga = (u, anno) => {
  const quote = quoteDellAnnata(u, anno);
  const totale = Number(quote.totalDue || 0);
  return { totale, dovuto: totale - totaleVersato(quote) };
};

const daSollecitarePagamenti = (u, anno) => {
  if (statoDi(u) === "ARCHIVED") return false;

  const inCoorte =
    tessereDelSoggetto(u?.contexts, anno, { stati: null }).length > 0 ||
    eAmministratore(u);
  if (!inCoorte) return false;

  if (statoDelSoggetto(u?.contexts, anno, { ripiegoPersona: statoDi(u) }) !== "ACTIVE") {
    return false;
  }

  return quoteDiRiga(u, anno).dovuto > 0;
};

/* =========================================================================
   RENEW MEMBERSHIP — chi non ha rinnovato per l'anno guardato
   =========================================================================
   ⚠️ LA REGOLA NON È QUI. «Non rinnovato per l'anno X» = la regola del titolo
   al rinnovo, `ruoliDaRinnovare` di backend/server.js (la stessa di POST
   /renew), che il server calcola in GET /users e consegna come
   `renewableSeasons` (gli anni per cui la persona avrebbe titolo). Il client
   legge soltanto se l'anno guardato c'è: nessuna ricopia della regola.
   Campo assente o non elenco = nessun titolo. Il server RIVALUTA TUTTO
   all'invio (POST /reminders/renew). */
const daSollecitareRinnovo = (u, anno) =>
  Number.isInteger(anno) &&
  Array.isArray(u?.renewableSeasons) &&
  u.renewableSeasons.includes(anno);

// I tutori del figlio: la sola faccia AUTOREVOLE (`guardians`), con la forma
// di `guardiansDaInput` del server (una voce non stringa → nessun tutore).
// `linkedProfileIds` sul genitore NON conta: il server non lo accetta.
const tutoriDi = (u) => {
  const grezzo = u?.guardians;
  if (!Array.isArray(grezzo) || !grezzo.every((v) => typeof v === "string")) return [];
  return grezzo.map((v) => v.trim()).filter(Boolean);
};

// Il testo leggibile di un rifiuto del server (`{ error }` delle rotte v2).
function testoDiErrore(err) {
  // D8B-FIX: il server può aver già spedito: non si invita a ripetere.
  if (err && err.timeout === true) return TIMEOUT_INVIO_MESSAGE;
  try {
    const corpo = JSON.parse(err && err.body);
    if (corpo && typeof corpo.error === "string" && corpo.error) return corpo.error;
  } catch (e) {
    // corpo non JSON: si ripiega sul testo generico qui sotto
  }
  return "Could not send the reminders. Please try again.";
}

export default function Reminders() {
  const { operatingSeason, enrollmentSeason, seasonStatus, enrollmentsOpen } = useAuth();

  const [users, setUsers] = useState([]);
  const [inCaricamento, setInCaricamento] = useState(true);
  const [errore, setErrore] = useState(null);

  const [stagioneScelta, setStagioneScelta] = useState(undefined);
  const [tipo, setTipo] = useState("complete-data");

  const [selezionati, setSelezionati] = useState(() => new Set());
  // Il testo del campo «Search»: filtra le righe per nome del destinatario.
  const [ricerca, setRicerca] = useState("");
  // Le righe già spedite spariscono PER LA SESSIONE della pagina: il flag
  // del server non cambia con l'invio, e senza questo ricomparirebbero.
  const [inviati, setInviati] = useState(() => new Set());
  const [inInvio, setInInvio] = useState(false);
  const [riepilogo, setRiepilogo] = useState(null);
  const [erroreInvio, setErroreInvio] = useState(null);

  // `GET /users` una volta al montaggio (come la User List).
  useEffect(() => {
    async function carica() {
      try {
        setInCaricamento(true);
        const dati = await api.get("/users");
        setUsers(Array.isArray(dati) ? dati : []);
        setErrore(null);
      } catch (err) {
        setUsers([]);
        setErrore("Could not load the member list. Please try again.");
      } finally {
        setInCaricamento(false);
      }
    }

    carica();
  }, []);

  /* I FIGLI DI UNA PERSONA — ⚠️ RICALCO DICHIARATO della logica famiglia di
     TeamAssignment.jsx (`indiceDiPersona`, `figliPerTutore`,
     `famigliaDiPersona`), locale a quel componente e fuori perimetro. Stesse
     due fonti: `guardians` sul figlio (faccia autorevole, indice inverso) e
     `linkedProfileIds` sul genitore (faccia debole, solo per ciò che la prima
     non ha dato); niente doppioni, niente auto-riferimento. Solo sugli id già
     in `users` di GET /users: nessuna lettura nuova.
     DIFFERENZE, volute: nessun filtro di stagione (si nominano tutti i figli
     risolti), id-fantasma taciuti, solo nomi senza squadra. */
  const indiceDiPersona = useMemo(() => {
    const perId = new Map();

    users.forEach((u) => {
      const id = String((u && u.id) || "");
      if (id) perId.set(id, u);
    });

    return perId;
  }, [users]);

  const figliPerTutore = useMemo(() => {
    const perTutore = new Map();

    users.forEach((figlio) => {
      const idFiglio = String((figlio && figlio.id) || "");
      if (!idFiglio) return;

      (Array.isArray(figlio.guardians) ? figlio.guardians : []).forEach(
        (idTutore) => {
          const chiave = String(idTutore || "");
          if (!chiave) return;
          if (!perTutore.has(chiave)) perTutore.set(chiave, []);
          perTutore.get(chiave).push(figlio);
        }
      );
    });

    return perTutore;
  }, [users]);

  /** I nomi dei figli risolti; elenco vuoto se non ce n'è nessuno. */
  const nomiDeiFigli = (u) => {
    const idPersona = String((u && u.id) || "");
    const figli = new Map();

    (figliPerTutore.get(idPersona) || []).forEach((figlio) => {
      const chiave = String((figlio && figlio.id) || "");
      if (chiave && chiave !== idPersona) figli.set(chiave, figlio);
    });

    (Array.isArray(u && u.linkedProfileIds) ? u.linkedProfileIds : []).forEach(
      (id) => {
        const chiave = String(id || "");
        if (!chiave || chiave === idPersona || figli.has(chiave)) return;
        figli.set(chiave, indiceDiPersona.get(chiave) || null);
      }
    );

    // Fantasmi (id senza scheda) e schede senza nome leggibile: taciuti.
    return Array.from(figli.values())
      .filter(Boolean)
      .map((figlio) => nomeDi(figlio))
      .filter(Boolean);
  };

  /* «SEASON VIEWED» — ricalco del pattern di UsersList.jsx
     (`annateDeiDati`, `stagioniDisponibili`, `stagioneAttiva`): anni =
     unione di operativo, iscrizione e anni dei dati. UNA DIFFERENZA, e
     voluta: il default è l'annata d'ISCRIZIONE, non l'operativa, perché
     «da completare» parla solo di quella. */
  const annateDeiDati = useMemo(
    () =>
      Array.from(
        users.reduce((elenco, u) => {
          const annate = Array.isArray(u?.seasons) ? u.seasons : [];
          annate.forEach((voce) => {
            const anno = annoDiLista(voce);
            if (anno !== null) elenco.add(anno);
          });
          const anno = annoDiLista(u?.season);
          if (anno !== null) elenco.add(anno);
          return elenco;
        }, new Set())
      ).sort((a, b) => b - a),
    [users]
  );

  const stagioniDisponibili = useMemo(
    () =>
      Array.from(
        [operatingSeason, enrollmentSeason].reduce(
          (elenco, anno) => (anno == null ? elenco : elenco.add(anno)),
          new Set(annateDeiDati)
        )
      ).sort((a, b) => b - a),
    [annateDeiDati, operatingSeason, enrollmentSeason]
  );

  const stagionePredefinita =
    enrollmentSeason != null
      ? enrollmentSeason
      : seasonStatus === "error"
        ? (annateDeiDati.length > 0 ? annateDeiDati[0] : null)
        : null;

  const stagioneAttiva =
    stagioneScelta !== undefined ? stagioneScelta : stagionePredefinita;

  // Stesso booleano della User List (`guardoAnnoIscrizione`): stesso metro
  // sui due lati, `Number.isInteger` prima dell'uguaglianza.
  const annoGuardato = annoDiLista(stagioneAttiva);
  const annoIscrizione = annoDiLista(enrollmentSeason);
  const guardoAnnoIscrizione =
    Number.isInteger(annoGuardato) && annoGuardato === annoIscrizione;

  // «Payments» (Y2) vale sull'anno operativo O su quello d'iscrizione: gli
  // stessi due che il server accetta come `season`.
  const annoOperativo = annoDiLista(operatingSeason);
  // «Renew membership»: la LISTA si vede su ogni anno del menù (decisione del
  // committente), l'INVIO solo sull'anno d'iscrizione (`inviabile`, qui
  // sotto): il rinnovo fa nascere la tessera di quell'anno, e «rinnova per
  // l'anno X» su un altro anno sarebbe un'email falsa.
  const guardoAnnoAmmesso =
    tipo === "payments"
      ? Number.isInteger(annoGuardato) &&
        (annoGuardato === annoOperativo || annoGuardato === annoIscrizione)
      : tipo === "renew"
        ? Number.isInteger(annoGuardato)
        : guardoAnnoIscrizione;

  // Iscrizioni chiuse: il sollecito inviterebbe a un rinnovo che POST /renew
  // rifiuta (409). Stesso metro tollerante di EnrolNextSeason: solo un `false`
  // esplicito è chiuso. La lista resta visibile, si spegne solo l'invio; la
  // rotta di sollecito fa lo stesso controllo sul server.
  const iscrizioniAperte = enrollmentsOpen !== false;

  const inviabile =
    tipo === "renew" ? guardoAnnoIscrizione && iscrizioniAperte : guardoAnnoAmmesso;

  // LA LISTA: la riga INCOMPLETE della User List (testata del file) + email.
  const righeCompleteData = useMemo(() => {
    if (tipo !== "complete-data" || !guardoAnnoIscrizione) return [];

    const statoDellAnno = (u) =>
      statoDelSoggetto(u?.contexts, stagioneAttiva, {
        ripiegoPersona: statoDi(u)
      });

    return users
      .filter(
        (u) =>
          tessereDelSoggetto(u?.contexts, stagioneAttiva, { stati: null }).length >
            0 || eAmministratore(u)
      )
      .filter((u) => {
        const stato = statoDellAnno(u);
        return stato !== "ARCHIVED" && stato !== "PENDING";
      })
      .filter((u) => u.mustCompleteSeason === true)
      // Mai sollecitare un ADMIN/SUPERADMIN: il soggetto non è un socio da inseguire.
      .filter((u) => !eAmministratore(u))
      .filter((u) => emailDi(u) !== "")
      .filter((u) => !haUnGenitore(u))
      .filter((u) => u?.id && !inviati.has(`${tipo}:${u.id}`))
      .sort(perCognome)
      .map((u) => ({ id: String(u.id), persona: u }));
  }, [users, stagioneAttiva, guardoAnnoIscrizione, inviati, tipo]);

  // LA LISTA PLAY FOOTBALL: i DESTINATARI REALI, non i soggetti. L'adulto da
  // sollecitare riceve lui; il minore (metro `eMinoreDaDob`) NON riceve, e al
  // suo posto riceve ogni tutore di `guardians` raggiungibile (non ARCHIVED,
  // non minore, con email). Un tutore con più figli = una riga, che li nomina
  // tutti; chi è adulto da sollecitare E tutore = una riga sola. Solo i dati
  // già in `users`: nessuna lettura nuova.
  const righePlayFootball = useMemo(() => {
    if (tipo !== "play-football" || !guardoAnnoIscrizione) return [];

    const oggi = oggiAPerth();
    const perDestinatario = new Map();

    const voceDi = (persona) => {
      const id = String(persona.id);
      if (!perDestinatario.has(id)) {
        perDestinatario.set(id, { id, persona, perSe: false, figli: [] });
      }
      return perDestinatario.get(id);
    };

    const raggiungibile = (p) =>
      Boolean(p?.id) &&
      statoDi(p) !== "ARCHIVED" &&
      !eMinoreDaDob(p, oggi) &&
      emailDi(p) !== "";

    users.forEach((u) => {
      if (!u?.id || !daSollecitarePlayFootball(u, annoGuardato)) return;

      if (eMinoreDaDob(u, oggi)) {
        tutoriDi(u).forEach((idTutore) => {
          const tutore = indiceDiPersona.get(idTutore);
          if (idTutore !== String(u.id) && raggiungibile(tutore)) {
            voceDi(tutore).figli.push(u);
          }
        });
      } else if (!eAmministratore(u) && raggiungibile(u)) {
        // L'adulto admin non si sollecita; un tutore admin, invece, resta un
        // destinatario valido per il promemoria del figlio (ramo qui sopra).
        voceDi(u).perSe = true;
      }
    });

    return Array.from(perDestinatario.values())
      .filter((r) => !inviati.has(`${tipo}:${r.id}`))
      .map((r) => ({ ...r, figli: r.figli.slice().sort(perCognome) }))
      .sort((a, b) => perCognome(a.persona, b.persona));
  }, [users, annoGuardato, guardoAnnoIscrizione, inviati, tipo, indiceDiPersona]);

  // LA LISTA PAYMENTS: i DESTINATARI REALI, sul modello di righePlayFootball.
  // L'adulto con saldo > 0 riceve lui (admin esclusi come soggetti); il minore
  // (`eMinoreDaDob`) NON riceve, e al suo posto riceve ogni tutore di
  // `guardians` raggiungibile. Una riga per destinatario; ogni riga porta, per
  // l'adulto (se deve per sé) e per ciascun figlio, dovuto e totale dell'anno
  // guardato (variante B).
  const righePagamenti = useMemo(() => {
    if (tipo !== "payments" || !guardoAnnoAmmesso) return [];

    const oggi = oggiAPerth();
    const perDestinatario = new Map();

    const voceDi = (persona) => {
      const id = String(persona.id);
      if (!perDestinatario.has(id)) {
        perDestinatario.set(id, { id, persona, perSe: false, figli: [] });
      }
      return perDestinatario.get(id);
    };

    const raggiungibile = (p) =>
      Boolean(p?.id) &&
      statoDi(p) !== "ARCHIVED" &&
      !eMinoreDaDob(p, oggi) &&
      emailDi(p) !== "";

    users.forEach((u) => {
      if (!u?.id || !daSollecitarePagamenti(u, annoGuardato)) return;

      if (eMinoreDaDob(u, oggi)) {
        tutoriDi(u).forEach((idTutore) => {
          const tutore = indiceDiPersona.get(idTutore);
          if (idTutore !== String(u.id) && raggiungibile(tutore)) {
            voceDi(tutore).figli.push(u);
          }
        });
      } else if (!eAmministratore(u) && raggiungibile(u)) {
        // L'adulto admin non si sollecita; un tutore admin resta un
        // destinatario valido per il saldo del figlio (ramo qui sopra).
        voceDi(u).perSe = true;
      }
    });

    return Array.from(perDestinatario.values())
      .filter((r) => !inviati.has(`${tipo}:${r.id}`))
      .map((r) => ({ ...r, figli: r.figli.slice().sort(perCognome) }))
      .sort((a, b) => perCognome(a.persona, b.persona));
  }, [users, annoGuardato, guardoAnnoAmmesso, inviati, tipo, indiceDiPersona]);

  // LA LISTA RENEW MEMBERSHIP: i DESTINATARI REALI, sul calco di
  // righePlayFootball. Soggetto = chi ha titolo al rinnovo per l'anno
  // guardato (`daSollecitareRinnovo`, cioè `renewableSeasons` dal server).
  // L'adulto riceve lui (admin esclusi come soggetti, come negli altri rami);
  // il minore (`eMinoreDaDob`) NON riceve, e al suo posto riceve ogni tutore di
  // `guardians` raggiungibile. Un adulto senza titolo proprio ma con figli da
  // rinnovare compare per conto dei figli (`perSe: false`).
  // ⚠️ DIFFERENZA VOLUTA dagli altri rami (decisione del committente): qui il
  // destinatario ARCHIVED NON si scarta — POST /renew ammette l'archiviato.
  // Restano: con email, non minore.
  const righeRinnovo = useMemo(() => {
    if (tipo !== "renew" || !guardoAnnoAmmesso) return [];

    const oggi = oggiAPerth();
    const perDestinatario = new Map();

    const voceDi = (persona) => {
      const id = String(persona.id);
      if (!perDestinatario.has(id)) {
        perDestinatario.set(id, { id, persona, perSe: false, figli: [] });
      }
      return perDestinatario.get(id);
    };

    const raggiungibile = (p) =>
      Boolean(p?.id) && !eMinoreDaDob(p, oggi) && emailDi(p) !== "";

    users.forEach((u) => {
      if (!u?.id || !daSollecitareRinnovo(u, annoGuardato)) return;

      if (eMinoreDaDob(u, oggi)) {
        tutoriDi(u).forEach((idTutore) => {
          const tutore = indiceDiPersona.get(idTutore);
          if (idTutore !== String(u.id) && raggiungibile(tutore)) {
            voceDi(tutore).figli.push(u);
          }
        });
      } else if (!eAmministratore(u) && raggiungibile(u)) {
        voceDi(u).perSe = true;
      }
    });

    return Array.from(perDestinatario.values())
      .filter((r) => !inviati.has(`${tipo}:${r.id}`))
      .map((r) => ({ ...r, figli: r.figli.slice().sort(perCognome) }))
      .sort((a, b) => perCognome(a.persona, b.persona));
  }, [users, annoGuardato, guardoAnnoAmmesso, inviati, tipo, indiceDiPersona]);

  const righeDelTipo =
    tipo === "payments"
      ? righePagamenti
      : tipo === "play-football"
        ? righePlayFootball
        : tipo === "renew"
          ? righeRinnovo
          : righeCompleteData;

  // LA RICERCA PER NOME, a valle del calcolo per tipo (metro della User List:
  // minuscole e «contiene» sul solo nome del destinatario, più il trim). Le
  // righe che restano SONO la lista: selezione, «Select all», tetto e invio
  // contano solo quelle — quello che vedi è quello che mandi.
  const cercato = ricerca.trim().toLowerCase();
  const righe =
    cercato === ""
      ? righeDelTipo
      : righeDelTipo.filter((r) =>
          nomeDi(r.persona).toLowerCase().includes(cercato)
        );

  // La selezione conta solo sulle righe a schermo: cambiando anno o tipo, o
  // dopo un invio, gli id che non si vedono più non partono.
  const idSelezionati = righe
    .map((r) => r.id)
    .filter((id) => selezionati.has(id));

  const tuttiSelezionati =
    righe.length > 0 && idSelezionati.length === righe.length;

  const alterna = (id) => {
    setSelezionati((prima) => {
      const dopo = new Set(prima);
      if (dopo.has(id)) dopo.delete(id);
      else dopo.add(id);
      return dopo;
    });
  };

  const alternaTutti = () => {
    setSelezionati(
      tuttiSelezionati ? new Set() : new Set(righe.map((r) => r.id))
    );
  };

  const cambiaTipo = (valore) => {
    setTipo(valore);
    setSelezionati(new Set());
    setRicerca("");
    setRiepilogo(null);
    setErroreInvio(null);
  };

  const oltreIlTetto = idSelezionati.length > TETTO_PER_INVIO;

  const spedisci = async () => {
    if (idSelezionati.length === 0 || oltreIlTetto || inInvio || !inviabile) return;

    // LA CONFERMA, comune ai due tipi: nomina la categoria e il numero.
    const quanti = idSelezionati.length;
    const tipoInviato = tipo;
    const conferma = window.confirm(
      `You are about to send the "${etichettaDelTipo(tipoInviato)}" reminder to ${quanti} ${quanti === 1 ? "recipient" : "recipients"}. Continue?`
    );
    if (!conferma) return;

    // IL SECONDO GATE: si digita SEND (trim, maiuscole indifferenti), o non
    // parte nulla — né chiamate né cambi di stato.
    const digitato = window.prompt("To confirm, type SEND:");
    if ((digitato || "").trim().toUpperCase() !== "SEND") return;

    setInInvio(true);
    setErroreInvio(null);
    setRiepilogo(null);

    try {
      const risposta =
        tipoInviato === "payments"
          ? await api.post("/reminders/payments", {
              season: annoGuardato,
              recipients: righe
                .filter((r) => selezionati.has(r.id))
                .map((r) => ({
                  id: r.id,
                  childIds: r.figli.map((f) => String(f.id))
                }))
            }, { timeoutMs: TIMEOUT_LUNGO_MS })
          : tipoInviato === "renew"
          ? await api.post("/reminders/renew", {
              recipients: righe
                .filter((r) => selezionati.has(r.id))
                .map((r) => ({
                  id: r.id,
                  childIds: r.figli.map((f) => String(f.id))
                }))
            }, { timeoutMs: TIMEOUT_LUNGO_MS })
          : tipoInviato === "play-football"
          ? await api.post("/reminders/play-football", {
              recipients: righe
                .filter((r) => selezionati.has(r.id))
                .map((r) => ({
                  id: r.id,
                  childIds: r.figli.map((f) => String(f.id))
                }))
            }, { timeoutMs: TIMEOUT_LUNGO_MS })
          : await api.post("/reminders/complete-data", {
              ids: idSelezionati
            }, { timeoutMs: TIMEOUT_LUNGO_MS });
      setRiepilogo(risposta && risposta.counts ? { ...risposta, tipo: tipoInviato } : null);

      const spediti = (Array.isArray(risposta?.results) ? risposta.results : [])
        .filter((voce) => voce && voce.result === "sent")
        .map((voce) => `${tipoInviato}:${voce.id}`);

      if (spediti.length > 0) {
        setInviati((prima) => new Set([...prima, ...spediti]));
      }
      setSelezionati(new Set());
      setRicerca("");
    } catch (err) {
      setErroreInvio(testoDiErrore(err));
    } finally {
      setInInvio(false);
    }
  };

  return (
    <HomeShell backTo="/home" width="admin">
      <h1 className="qpsc-page-title">Member Reminders</h1>
      <p className="qpsc-note rm-centered">Emails to members, sent by hand</p>

      <SeasonStatusBar />

      <section className="rm-page">
        <div className="rm-controls">
          <div className="qpsc-field rm-field">
            <span className="qpsc-field__label">Season Viewed</span>
            <select
              className="qpsc-select"
              aria-label="Season viewed"
              value={stagioneAttiva === null ? "" : String(stagioneAttiva)}
              onChange={(e) => setStagioneScelta(Number(e.target.value))}
            >
              {stagioniDisponibili.map((anno) => (
                <option key={anno} value={String(anno)}>{anno}</option>
              ))}
            </select>
          </div>

          <div className="qpsc-field rm-field">
            <span className="qpsc-field__label">Reminder</span>
            <select
              className="qpsc-select"
              aria-label="Reminder type"
              value={tipo}
              onChange={(e) => cambiaTipo(e.target.value)}
            >
              {TIPI.map(({ valore, etichetta, spento }) => (
                <option key={valore} value={valore} disabled={spento}>
                  {spento ? `${etichetta} — coming soon` : etichetta}
                </option>
              ))}
            </select>
          </div>

          <div className="qpsc-field rm-field">
            <span className="qpsc-field__label">Search</span>
            <input
              className="qpsc-input"
              type="text"
              aria-label="Search by name"
              placeholder="Search by name"
              value={ricerca}
              onChange={(e) => setRicerca(e.target.value)}
            />
          </div>
        </div>

        {errore && <p className="rm-error">{errore}</p>}

        {!errore && inCaricamento && <p className="rm-hint">Loading…</p>}

        {!errore && !inCaricamento && !guardoAnnoAmmesso && (
          tipo === "payments" ? (
            <p className="rm-hint">
              «{etichettaDelTipo(tipo)}» applies only to the operating season
              {Number.isInteger(annoOperativo) ? ` (${annoOperativo})` : ""} or the
              enrollment season
              {Number.isInteger(annoIscrizione) ? ` (${annoIscrizione})` : ""}.
              Choose one of them to see the members to remind.
            </p>
          ) : (
            <p className="rm-hint">
              «{etichettaDelTipo(tipo)}» applies only to the enrollment season
              {Number.isInteger(annoIscrizione) ? ` (${annoIscrizione})` : ""}.
              Choose that season to see the members to remind.
            </p>
          )
        )}

        {!errore && !inCaricamento && guardoAnnoAmmesso && (
          <>
            {tipo === "renew" ? (
              <>
                <p className="rm-hint">
                  Members who have not renewed for {annoGuardato}: they had a
                  membership in an earlier season and none yet for{" "}
                  {annoGuardato}. Archived members are included. Minors do not
                  receive the reminder: their parent does, with the children
                  named.
                </p>
                {!guardoAnnoIscrizione && (
                  <p className="rm-hint">
                    «{etichettaDelTipo(tipo)}» applies only to the enrollment season
                    {Number.isInteger(annoIscrizione) ? ` (${annoIscrizione})` : ""}.
                    The list is shown for this season, but reminders can be sent
                    only for that one.
                  </p>
                )}
                {!iscrizioniAperte && (
                  <p className="rm-hint">
                    Enrollments are currently closed: the list is shown, but
                    renewal reminders can be sent only while enrollments are open.
                  </p>
                )}
              </>
            ) : tipo === "payments" ? (
              <p className="rm-hint">
                Members with an outstanding balance for {annoGuardato} (amount
                due / total fees). Minors do not receive the reminder: their
                parent does, with each child&apos;s balance.
              </p>
            ) : tipo === "play-football" ? (
              <p className="rm-hint">
                Players and coaches not yet registered for Play Football for{" "}
                {annoIscrizione}. Minors do not receive the reminder: their
                parent does, with the children named.
              </p>
            ) : (
              <p className="rm-hint">
                Members shown as INCOMPLETE in the User List for {annoIscrizione},
                with an email address. Members linked to a parent are not listed.
              </p>
            )}

            {righe.length === 0 ? (
              <p className="rm-hint">
                {righeDelTipo.length > 0
                  ? "No members match the search."
                  : "No members to remind."}
              </p>
            ) : (
              <>
                <label className="qpsc-check rm-all">
                  <input
                    type="checkbox"
                    className="qpsc-check__input"
                    checked={tuttiSelezionati}
                    onChange={alternaTutti}
                  />
                  <span className="qpsc-check__box" aria-hidden="true" />
                  Select all ({righe.length})
                </label>

                <ul className="rm-list">
                  {righe.map((riga) => {
                    const { id, persona: u } = riga;
                    const pf = tipo === "play-football";
                    const pay = tipo === "payments";
                    const rinnovo = tipo === "renew";
                    // Variante B: «[nome]: dovuto/totale» per sé e per figlio.
                    const saldi = pay
                      ? [...(riga.perSe ? [u] : []), ...riga.figli].map((p) => ({
                          id: String(p.id),
                          nome: nomeDi(p) || "—",
                          ...quoteDiRiga(p, annoGuardato)
                        }))
                      : [];
                    const squadre = squadreDelSoggetto(u?.contexts, stagioneAttiva, {
                      assorbeTutte: false,
                      doppioni: "normalizzati"
                    });
                    const figli = pf || pay || rinnovo ? [] : nomiDeiFigli(u);
                    const daRegistrare = pf || rinnovo
                      ? [
                          ...(riga.perSe && riga.figli.length > 0 ? ["themselves"] : []),
                          ...riga.figli.map((f) => nomeDi(f) || "—")
                        ]
                      : [];
                    return (
                      <li key={id} className="rm-item">
                        <label className="qpsc-check rm-row">
                          <input
                            type="checkbox"
                            className="qpsc-check__input"
                            checked={selezionati.has(id)}
                            onChange={() => alterna(id)}
                          />
                          <span className="qpsc-check__box" aria-hidden="true" />
                          <span className="rm-row__text">
                            <span className="rm-row__name">{nomeDi(u) || "—"}</span>
                            <span className="rm-row__meta">{emailDi(u)}</span>
                            {figli.length > 0 && (
                              <span className="rm-row__meta">
                                {figli.length === 1 ? "Child" : "Children"}:{" "}
                                {figli.join(", ")}
                              </span>
                            )}
                            {pf && riga.figli.length > 0 && (
                              <span className="rm-row__meta">
                                Registering: {daRegistrare.join(", ")}
                              </span>
                            )}
                            {rinnovo && riga.figli.length > 0 && (
                              <span className="rm-row__meta">
                                Renewing: {daRegistrare.join(", ")}
                              </span>
                            )}
                            {saldi.map((s) => (
                              <span key={s.id} className="rm-row__meta">
                                {s.nome}:{" "}
                                <span className="rm-amount--due">{denaroAschermo(s.dovuto)}</span>
                                {" / "}
                                <span className="rm-amount--total">{denaroAschermo(s.totale)}</span>
                              </span>
                            ))}
                            {!pf && !pay && !rinnovo && (
                              <span className="rm-row__meta">
                                {squadre.length > 0 ? squadre.join(", ") : "No team"}
                              </span>
                            )}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}

            {oltreIlTetto && (
              <p className="rm-error">
                At most {TETTO_PER_INVIO} members per send: please select fewer.
              </p>
            )}

            <div className="rm-actions">
              <button
                type="button"
                className="qpsc-tool-btn"
                onClick={spedisci}
                disabled={idSelezionati.length === 0 || oltreIlTetto || inInvio || !inviabile}
              >
                {inInvio ? "Sending…" : `Send (${idSelezionati.length})`}
              </button>
            </div>
          </>
        )}

        {erroreInvio && <p className="rm-error">{erroreInvio}</p>}

        {riepilogo && (
          <div className="rm-summary" role="status">
            <p className="rm-summary__title">
              Summary — {riepilogo.total} selected
            </p>
            <ul className="rm-summary__list">
              {ESITI.filter(({ chiave }) => riepilogo.counts[chiave] > 0).map(
                ({ chiave, etichetta, etichettaPF, etichettaPay, etichettaRenew }) => (
                  <li key={chiave}>
                    {(riepilogo.tipo === "play-football" && etichettaPF) ||
                      (riepilogo.tipo === "payments" && etichettaPay) ||
                      (riepilogo.tipo === "renew" && etichettaRenew) ||
                      etichetta}:{" "}
                    {riepilogo.counts[chiave]}
                  </li>
                )
              )}
              {riepilogo.children && riepilogo.children.included > 0 && (
                <li>Children named in the emails sent: {riepilogo.children.included}</li>
              )}
              {riepilogo.children && riepilogo.children.skipped > 0 && (
                <li>Children skipped by the server check: {riepilogo.children.skipped}</li>
              )}
              {AVVISO[riepilogo.trackingNotice] && (
                <li>Tracking notice to the club: {AVVISO[riepilogo.trackingNotice]}</li>
              )}
            </ul>
          </div>
        )}
      </section>
    </HomeShell>
  );
}
